import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AuthContext } from '../App';
import { useTenant } from '../context/TenantContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Settings as SettingsIcon, Check, X, AlertCircle, Bell, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import MultiSelectAutocomplete from '../components/MultiSelectAutocomplete';
import LogoUpload from '../components/LogoUpload';
import CustomizationSimple from '../components/CustomizationSimple';

const Settings = () => {
  const { t } = useTranslation();
  const { token, API, user } = useContext(AuthContext);
  const { tenant, isFeatureEnabled, PLAN_FEATURES, fetchTenantInfo } = useTenant();
  const [config, setConfig] = useState(null);
  const [tenantSettings, setTenantSettings] = useState(null);
  const [languages, setLanguages] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [emailPrefs, setEmailPrefs] = useState({
    app_notifications: true,
    product_updates: false,
    marketing_emails: false,
  });
  const [emailPrefsSaving, setEmailPrefsSaving] = useState(false);
  const [stats, setStats] = useState({
    users: 0,
    tables: 0,
    menuItems: 0,
  });

  useEffect(() => {
    fetchConfig();
    fetchLanguages();
    fetchStats();
    fetchEmailPrefs();
  }, []);

  // Fetch tenant settings when tenant becomes available
  useEffect(() => {
    if (tenant && tenant.id) {
      fetchTenantSettings();
      fetchStats();
    }
  }, [tenant?.id]);

  const fetchLanguages = async () => {
    try {
      const res = await axios.get(`${API}/languages`);
      setLanguages(res.data.available || {});
    } catch (error) {
      console.error('Error loading languages');
    }
  };

  const fetchStats = async () => {
    // Only fetch stats if we have token and tenant
    if (!token || !tenant?.id) {
      return;
    }
    
    try {
      const res = await axios.get(`${API}/tenants/usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats({
        users: res.data.users?.current || 0,
        tables: res.data.tables?.current || 0,
        menuItems: res.data.menu_items?.current || 0,
      });
    } catch (error) {
      console.error('Error loading stats:', error);
      // Don't show error toast for stats, just log it
    }
  };

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/config`);
      setConfig(res.data);
    } catch (error) {
      toast.error(t('messages.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailPrefs = async () => {
    try {
      const res = await axios.get(`${API}/auth/email-preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailPrefs(res.data);
    } catch (error) {
      console.error('Error loading email preferences:', error);
    }
  };

  const saveEmailPrefs = async (newPrefs) => {
    setEmailPrefsSaving(true);
    try {
      const res = await axios.put(`${API}/auth/email-preferences`, newPrefs, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailPrefs(res.data);
      toast.success(t('settings.emailPrefs.saved'));
    } catch (error) {
      toast.error(t('messages.settingsFailed'));
    } finally {
      setEmailPrefsSaving(false);
    }
  };

  const handleEmailPrefToggle = (key) => {
    if (key === 'app_notifications') return; // Can't disable transactional
    const updated = { ...emailPrefs, [key]: !emailPrefs[key] };
    setEmailPrefs(updated);
    saveEmailPrefs(updated);
  };

  const fetchTenantSettings = async () => {
    try {
      if (!tenant || !tenant.id) {
        console.warn('Tenant ID not available');
        return;
      }
      const res = await axios.get(`${API}/tenant/${tenant.id}/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenantSettings(res.data);
    } catch (error) {
      console.error('Error loading tenant settings:', error);
      toast.error(t('messages.errorLoading'));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save global config (system-wide settings only, NOT restaurant_name which is per-tenant)
      const globalPayload = {
        currency: config?.currency,
        currency_symbol: config?.currency_symbol,
        google_analytics_id: config?.google_analytics_id,
        supported_languages: config?.supported_languages,
        default_language: config?.default_language,
      };
      await axios.put(`${API}/config`, globalPayload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Save tenant-specific settings
      if (tenantSettings && tenant && tenant.id) {
        const tenantPayload = {
          restaurant_name: tenantSettings.restaurant_name,
          supported_languages: tenantSettings.supported_languages || ['tr'],
          default_language: tenantSettings.default_language || 'tr',
          currency: tenantSettings.currency,
          currency_symbol: tenantSettings.currency_symbol,
          default_vat_percentage: tenantSettings.default_vat_percentage || 20.0,
          // Contact & Social Media fields
          address: tenantSettings.address,
          website: tenantSettings.website,
          telephone: tenantSettings.telephone,
          gsm_number: tenantSettings.gsm_number,
          whatsapp_enabled: tenantSettings.whatsapp_enabled || false,
          location_url: tenantSettings.location_url,
          instagram_url: tenantSettings.instagram_url,
          twitter_url: tenantSettings.twitter_url,
          facebook_url: tenantSettings.facebook_url,
          timezone: tenantSettings.timezone || 'UTC',
        };
        await axios.put(`${API}/tenant/${tenant.id}/settings`, tenantPayload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      toast.success(t('messages.settingsSaved'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('messages.settingsFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center p-6">{t('messages.loading')}</div>;
  }

  const restaurantNameValue = tenantSettings?.restaurant_name || config?.restaurant_name || '';
  const supportedLangs = tenantSettings?.supported_languages || config?.supported_languages || ['tr'];
  const defaultLang = tenantSettings?.default_language || config?.default_language || 'tr';
  const plan = tenant?.plan || 'FREE';
  const tenantName = tenant?.name || 'Tenant';
  const tenantId = tenant?.id || '—';
  const tenantSlug = tenant?.slug || '';
  const planFeatures = PLAN_FEATURES[plan] || {};

  return (
    <div className="settings">
      <div className="section-header mb-6">
        <div className="flex items-center gap-3">
          <SettingsIcon size={24} />
          <h2 className="section-title">Business Settings</h2>
        </div>
      </div>

      {/* Tab Navigation and Content */}
      <Tabs defaultValue="general" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="general">
            {t('settings.general') || 'General'}
          </TabsTrigger>
          <TabsTrigger value="customization">
            {t('settings.customization') || 'Customization'}
          </TabsTrigger>
          <TabsTrigger value="notifications">
            <Bell size={14} className="mr-1" />
            {t('settings.emailPrefs.title') || 'Notifications'}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="general">
        <div style={{ marginBottom: '24px' }}>
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-lg">{tenantName}</h3>
                <p className="text-sm text-gray-600">Tenant ID: {tenantId}</p>
              </div>
              <Link to="/admin/upgrade" className="no-underline">
                <Badge style={{ fontSize: '12px', padding: '8px 12px', cursor: 'pointer' }} className="hover:bg-blue-100">
                  {plan} Plan → Manage
                </Badge>
              </Link>
            </div>

            {/* Usage Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginTop: '16px' }}>
              <div style={{ textAlign: 'center', padding: '12px', background: '#f9fafb', borderRadius: '8px' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{stats.users}/{planFeatures?.maxUsers || 5}</div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>Users</div>
              </div>
              <div style={{ textAlign: 'center', padding: '12px', background: '#f9fafb', borderRadius: '8px' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{stats.menuItems}/{planFeatures?.maxItems || 50}</div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>Menu Items</div>
              </div>
            </div>

            {/* Note: Features section hidden - tables, orders, inventory, loyalty are out of scope */}

            {/* Public Menu Permalink */}
            <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
              <p style={{ fontSize: '12px', fontWeight: '500', marginBottom: '8px', color: '#374151' }}>{t('settings.publicMenuPermalink')}</p>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input 
                  type="text" 
                  readOnly 
                  value={tenantSlug ? `${window.location.origin}/menu/${tenantSlug}` : t('settings.slugMissing')}
                  style={{ 
                    flex: 1, 
                    padding: '8px 12px', 
                    fontSize: '12px', 
                    border: '1px solid #e5e7eb', 
                    borderRadius: '6px',
                    background: '#f9fafb',
                    fontFamily: 'monospace'
                  }}
                  onClick={(e) => e.target.select()}
                />
                <Button 
                  size="sm"
                  disabled={!tenantSlug}
                  onClick={() => {
                    if (!tenantSlug) return;
                    navigator.clipboard.writeText(`${window.location.origin}/menu/${tenantSlug}`);
                    toast.success(t('settings.linkCopied'));
                  }}
                >
                  {t('settings.copy')}
                </Button>
                <Button 
                  size="sm"
                  variant="outline"
                  disabled={!tenantSlug}
                  onClick={() => tenantSlug && window.open(`/menu/${tenantSlug}`, '_blank')}
                >
                  {t('settings.preview')}
                </Button>
              </div>
              <p style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                {t('settings.shareLinkHelp')}
              </p>
            </div>
          </div>
        </div>

        <div className="settings-grid">
        {/* Business Information */}
        <div className="card">
          <h3 className="card-title">{t('settings.restaurantInformation')}</h3>
          <div className="form-group">
            <label>{t('settings.restaurantName')}</label>
            <input
              type="text"
              value={restaurantNameValue}
              onChange={(e) => {
                const val = e.target.value;
                setTenantSettings(prev => ({ ...(prev || {}), restaurant_name: val }));
              }}
              placeholder={t('settings.restaurantNamePlaceholder')}
              data-testid="restaurant-name-input"
            />
            <small className="text-gray-500">{t('settings.restaurantNameHelp')}</small>
          </div>

          {/* Logo Upload Component */}
          <div className="form-group">
            <LogoUpload 
              currentLogo={tenant?.logo_url} 
              onLogoUpdate={(logoUrl) => {
                toast.success(t('messages.logoUpdated'));
                // Refresh tenant data to get updated logo_url
                fetchTenantInfo();
              }}
              disabled={!isFeatureEnabled('customLogo')}
            />
          </div>

          <div className="form-group">
            <label>{t('settings.supportedLanguages')}</label>
            <MultiSelectAutocomplete
              options={languages}
              value={supportedLangs}
              maxSelections={['PRO', 'ENTERPRISE'].includes(plan) ? null : 1}
              placeholder={t('settings.selectLanguages') || 'Select languages...'}
              searchPlaceholder={t('settings.searchLanguages') || 'Search languages...'}
              onChange={(newLangs) => {
                setTenantSettings(prev => ({ ...(prev || {}), supported_languages: newLangs }));
              }}
              onMaxLimitReached={() => {
                toast.error(t('settings.multiLanguageUpgrade') || 'Your plan supports only one language');
              }}
              testId="supported-languages-select"
            />
            <small className="text-gray-500" style={{ marginTop: '8px', display: 'block' }}>
              {['PRO', 'ENTERPRISE'].includes(plan)
                ? t('settings.supportedLanguagesHelp')
                : 'Your plan allows one language. Upgrade to add more.'}
            </small>
          </div>

          {tenant?.logo_url && (
            <div className="logo-preview">
              <label>{t('settings.logoPreview')}</label>
              <img 
                src={`${API.replace('/api', '')}${tenant.logo_url}`}
                alt="Business Logo" 
                onError={(e) => e.target.style.display = 'none'}
                data-testid="logo-preview"
              />
            </div>
          )}
        </div>

        {/* Contact & Social Media */}
        <div className="card">
          <h3 className="card-title">{t('settings.contactInformation')}</h3>
          <p className="text-gray-500" style={{ marginBottom: '20px', fontSize: '14px' }}>
            {t('settings.contactHelp')}
          </p>
          
          <div className="form-group">
            <label>{t('settings.address')}</label>
            <input
              type="text"
              value={tenantSettings?.address || ''}
              onChange={(e) => setTenantSettings(prev => ({ ...(prev || {}), address: e.target.value }))}
              placeholder={t('settings.addressPlaceholder')}
            />
          </div>

          <div className="form-group">
            <label>{t('settings.website')}</label>
            <input
              type="url"
              value={tenantSettings?.website || ''}
              onChange={(e) => setTenantSettings(prev => ({ ...(prev || {}), website: e.target.value }))}
              placeholder={t('settings.websitePlaceholder')}
            />
          </div>

          <div className="form-group">
            <label>{t('settings.telephone')}</label>
            <input
              type="tel"
              value={tenantSettings?.telephone || ''}
              onChange={(e) => setTenantSettings(prev => ({ ...(prev || {}), telephone: e.target.value }))}
              placeholder={t('settings.telephonePlaceholder')}
            />
          </div>

          <div className="form-group">
            <label>{t('settings.mobile')}</label>
            <input
              type="tel"
              value={tenantSettings?.gsm_number || ''}
              onChange={(e) => setTenantSettings(prev => ({ ...(prev || {}), gsm_number: e.target.value }))}
              placeholder={t('settings.mobilePlaceholder')}
            />
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={tenantSettings?.whatsapp_enabled || false}
                onChange={(e) => setTenantSettings(prev => ({ ...(prev || {}), whatsapp_enabled: e.target.checked }))}
              />
              <span>{t('settings.whatsappEnabled')}</span>
            </label>
            <small className="text-gray-500">{t('settings.whatsappHelp')}</small>
          </div>

          <div className="form-group">
            <label>{t('settings.locationUrl')}</label>
            <input
              type="url"
              value={tenantSettings?.location_url || ''}
              onChange={(e) => setTenantSettings(prev => ({ ...(prev || {}), location_url: e.target.value }))}
              placeholder={t('settings.locationUrlPlaceholder')}
            />
            <small className="text-gray-500">{t('settings.locationUrlHelp')}</small>
          </div>

          <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid #e5e7eb' }} />

          <div className="form-group">
            <label>{t('settings.instagramUrl')}</label>
            <input
              type="url"
              value={tenantSettings?.instagram_url || ''}
              onChange={(e) => setTenantSettings(prev => ({ ...(prev || {}), instagram_url: e.target.value }))}
              placeholder={t('settings.instagramPlaceholder')}
            />
          </div>

          <div className="form-group">
            <label>{t('settings.twitterUrl')}</label>
            <input
              type="url"
              value={tenantSettings?.twitter_url || ''}
              onChange={(e) => setTenantSettings(prev => ({ ...(prev || {}), twitter_url: e.target.value }))}
              placeholder={t('settings.twitterPlaceholder')}
            />
          </div>

          <div className="form-group">
            <label>{t('settings.facebookUrl')}</label>
            <input
              type="url"
              value={tenantSettings?.facebook_url || ''}
              onChange={(e) => setTenantSettings(prev => ({ ...(prev || {}), facebook_url: e.target.value }))}
              placeholder={t('settings.facebookPlaceholder')}
            />
          </div>
        </div>

        {/* VAT Settings */}
        <div className="card">
          <h3 className="card-title">{t('settings.taxSettings')}</h3>
          <div className="form-group">
            <label>{t('settings.defaultVatPercentage')}</label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="100"
              value={tenantSettings?.default_vat_percentage || 20}
              onChange={(e) => setTenantSettings(prev => ({ ...(prev || {}), default_vat_percentage: parseFloat(e.target.value) }))}
              data-testid="vat-percentage-input"
            />
            <small className="text-gray-500">{t('settings.defaultVatHelp')}</small>
          </div>

          <div className="info-box">
            <p><strong>Currency:</strong> {config?.currency} ({config?.currency_symbol})</p>
          </div>
        </div>

        {/* Language & Regional Settings */}
        <div className="card">
          <h3 className="card-title">{t('settings.languageSettings')}</h3>
          <div className="form-group">
            <label>{t('settings.defaultLanguage')}</label>
            <select
              value={defaultLang}
              onChange={(e) => setTenantSettings(prev => ({ ...(prev || {}), default_language: e.target.value }))}
              data-testid="default-language-select"
            >
              {supportedLangs.map(code => {
                const langInfo = languages[code] || {};
                return (
                  <option key={code} value={code}>
                    {langInfo.native_name || code}
                  </option>
                );
              })}
            </select>
            <small className="text-gray-500">Default language for public menu (used when browser language is not TR/EN)</small>
          </div>

          <div className="form-group">
            <label>{t('settings.timezone')}</label>
            <select
              value={tenantSettings?.timezone || 'UTC'}
              onChange={(e) => setTenantSettings(prev => ({ ...(prev || {}), timezone: e.target.value }))}
              data-testid="timezone-select"
            >
              {(() => {
                const common = [
                  'UTC',
                  'Europe/Istanbul',
                  'Europe/London',
                  'Europe/Paris',
                  'Europe/Berlin',
                  'Europe/Moscow',
                  'America/New_York',
                  'America/Chicago',
                  'America/Denver',
                  'America/Los_Angeles',
                  'America/Sao_Paulo',
                  'Asia/Dubai',
                  'Asia/Kolkata',
                  'Asia/Shanghai',
                  'Asia/Tokyo',
                  'Asia/Seoul',
                  'Australia/Sydney',
                  'Pacific/Auckland',
                  'Africa/Cairo',
                ];
                const commonSet = new Set(common);
                let allTimezones = [];
                try {
                  allTimezones = Intl.supportedValuesOf('timeZone');
                } catch {
                  allTimezones = [];
                }
                const rest = allTimezones.filter(tz => !commonSet.has(tz));
                return (
                  <>
                    <optgroup label="— Common —">
                      {common.map(tz => (
                        <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
                      ))}
                    </optgroup>
                    {rest.length > 0 && (
                      <optgroup label="— All Timezones —">
                        {rest.map(tz => (
                          <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
                        ))}
                      </optgroup>
                    )}
                  </>
                );
              })()}
            </select>
            <small className="text-gray-500">{t('settings.timezoneHelp')}</small>
          </div>
        </div>
      </div>

      <div className="save-section">
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="save-btn"
          data-testid="save-settings-btn"
        >
          <Save size={18} />
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
        </TabsContent>

        <TabsContent value="customization">
          <CustomizationSimple />
        </TabsContent>

        <TabsContent value="notifications">
          <div className="card" style={{ maxWidth: '600px' }}>
            <div className="flex items-center gap-2 mb-2">
              <Mail size={20} />
              <h3 className="card-title" style={{ marginBottom: 0 }}>{t('settings.emailPrefs.heading')}</h3>
            </div>
            <p className="text-sm" style={{ color: 'hsl(var(--muted-foreground))', marginBottom: '24px' }}>
              {t('settings.emailPrefs.description')}
            </p>

            {/* App Notifications — always on */}
            <div className="email-pref-row">
              <div className="email-pref-info">
                <span className="email-pref-label">{t('settings.emailPrefs.appNotifications')}</span>
                <span className="email-pref-desc">{t('settings.emailPrefs.appNotificationsDesc')}</span>
              </div>
              <label className="toggle-switch" title={t('settings.emailPrefs.alwaysOn')}>
                <input type="checkbox" checked disabled />
                <span className="toggle-slider"></span>
              </label>
            </div>

            {/* Product Updates */}
            <div className="email-pref-row">
              <div className="email-pref-info">
                <span className="email-pref-label">{t('settings.emailPrefs.productUpdates')}</span>
                <span className="email-pref-desc">{t('settings.emailPrefs.productUpdatesDesc')}</span>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={emailPrefs.product_updates}
                  onChange={() => handleEmailPrefToggle('product_updates')}
                  disabled={emailPrefsSaving}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            {/* Marketing Emails */}
            <div className="email-pref-row">
              <div className="email-pref-info">
                <span className="email-pref-label">{t('settings.emailPrefs.marketingEmails')}</span>
                <span className="email-pref-desc">{t('settings.emailPrefs.marketingEmailsDesc')}</span>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={emailPrefs.marketing_emails}
                  onChange={() => handleEmailPrefToggle('marketing_emails')}
                  disabled={emailPrefsSaving}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <p className="text-xs" style={{ color: 'hsl(var(--muted-foreground))', marginTop: '20px' }}>
              {t('settings.emailPrefs.gdprNote')}
            </p>
          </div>
        </TabsContent>
      </Tabs>

      <style>{`
        .settings {
          width: 100%;
        }

        .section-header {
          margin-bottom: 24px;
        }

        .section-title {
          font-size: 20px;
          font-weight: 600;
          margin: 0;
        }

        .settings-tabs {
          display: flex;
          gap: 0;
          margin-bottom: 32px;
          border-bottom: 2px solid hsl(var(--border));
        }

        .tab-button {
          padding: 12px 24px;
          border: none;
          background: none;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          color: hsl(var(--muted-foreground));
          transition: all 0.2s;
          border-bottom: 3px solid transparent;
          margin-bottom: -2px;
        }

        .tab-button:hover {
          color: hsl(var(--foreground));
          background: hsl(var(--muted) / 0.5);
        }

        .tab-button.active {
          color: hsl(var(--primary));
          border-bottom-color: hsl(var(--primary));
        }

        .customization-content {
          margin-bottom: 32px;
        }

        .settings-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 24px;
          margin-bottom: 32px;
        }

        .card {
          background: hsl(var(--card));
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
          border: 1px solid hsl(var(--border));
          transition: background-color 0.3s;
        }

        .card-title {
          font-size: 18px;
          font-weight: 600;
          margin-bottom: 20px;
          color: hsl(var(--foreground));
          font-family: 'Manrope', sans-serif;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-group label {
          display: block;
          font-size: 14px;
          font-weight: 600;
          color: hsl(var(--foreground));
          margin-bottom: 8px;
        }

        .form-group input,
        .form-group select {
          width: 100%;
          padding: 10px 14px;
          border: 1px solid hsl(var(--border));
          border-radius: 8px;
          font-size: 14px;
          transition: border-color 0.2s, background-color 0.3s;
          background: hsl(var(--background));
          color: hsl(var(--foreground));
        }

        .form-group input:focus,
        .form-group select:focus {
          border-color: hsl(var(--primary));
          outline: none;
        }

        .form-group small {
          display: block;
          margin-top: 6px;
          font-size: 12px;
          color: hsl(var(--muted-foreground));
        }

        .logo-preview {
          margin-top: 16px;
          padding: 16px;
          background: hsl(var(--muted));
          border-radius: 8px;
        }

        .logo-preview label {
          display: block;
          font-size: 13px;
          font-weight: 600;
          color: hsl(var(--muted-foreground));
          margin-bottom: 12px;
        }

        .logo-preview img {
          max-width: 200px;
          max-height: 120px;
          border-radius: 8px;
          border: 2px solid hsl(var(--border));
        }

        .info-box {
          padding: 12px 16px;
          background: hsl(var(--primary) / 0.1);
          border-left: 4px solid hsl(var(--primary));
          border-radius: 8px;
          margin-top: 16px;
        }

        .info-box p {
          margin: 0;
          font-size: 14px;
          color: hsl(var(--primary));
        }

        .save-section {
          display: flex;
          justify-content: flex-end;
          padding: 20px 0;
        }

        .save-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 32px;
          font-size: 15px;
        }

        @media (max-width: 768px) {
          .settings-grid {
            grid-template-columns: 1fr;
          }

          .settings-tabs {
            overflow-x: auto;
          }
        }

        /* Email preferences toggle styles */
        .email-pref-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 0;
          border-bottom: 1px solid hsl(var(--border));
        }

        .email-pref-row:last-of-type {
          border-bottom: none;
        }

        .email-pref-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
          flex: 1;
          margin-right: 16px;
        }

        .email-pref-label {
          font-size: 14px;
          font-weight: 600;
          color: hsl(var(--foreground));
        }

        .email-pref-desc {
          font-size: 12px;
          color: hsl(var(--muted-foreground));
        }

        .toggle-switch {
          position: relative;
          display: inline-block;
          width: 44px;
          height: 24px;
          flex-shrink: 0;
          cursor: pointer;
        }

        .toggle-switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }

        .toggle-slider {
          position: absolute;
          inset: 0;
          background-color: hsl(var(--muted));
          border-radius: 24px;
          transition: background-color 0.2s;
        }

        .toggle-slider::before {
          content: '';
          position: absolute;
          height: 18px;
          width: 18px;
          left: 3px;
          bottom: 3px;
          background-color: white;
          border-radius: 50%;
          transition: transform 0.2s;
        }

        .toggle-switch input:checked + .toggle-slider {
          background-color: hsl(var(--primary));
        }

        .toggle-switch input:checked + .toggle-slider::before {
          transform: translateX(20px);
        }

        .toggle-switch input:disabled + .toggle-slider {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default Settings;
